import { useEffect, useRef } from "react";

import { oidcAuthority, oidcClientId } from "authentication/constants";
import { useSigninRedirect } from "authentication/useSigninRedirect";
import { AUTH_WIDGET_ID } from "common/constants/authWidgetId";
import { LanguageCode } from "common/types/languageCode";
import { onAuthWidgetLoad } from "common/utilities/onAuthWidgetLoad";
import { useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { getSiteLanguage } from "domains/language/selectors";

export const useAuthInit = () => {
  const signinRedirect = useSigninRedirect();
  const siteLanguage = useSelector(getSiteLanguage);
  const track = useTrack();

  const languageRef = useRef<typeof languageId | undefined>(undefined);
  // the Speedy Sign In widget uses 'cn' instead of 'zh'
  const languageId = siteLanguage === LanguageCode.ZH ? "cn" : siteLanguage;

  useEffect(() => {
    if (languageId === languageRef.current) {
      return;
    }

    languageRef.current = languageId;

    // Remove this and just pass the value directly once we are ready to roll out, and remove VITE_AUTH_WIDGET_USE_SOCIAL_SIGNUP_URL env var
    const socialSignupConfiguration =
      import.meta.env.VITE_AUTH_WIDGET_USE_SOCIAL_SIGNUP_URL === "true"
        ? { socialSignupUrl: `${oidcAuthority}/socialSignup` }
        : {};

    const config = {
      rootSelector: `#${AUTH_WIDGET_ID}`,
      apiKey: import.meta.env.VITE_AUTH_WIDGET_API_KEY,
      appName: import.meta.env.VITE_AUTH_WIDGET_APP_NAME,
      apiBaseUrl: import.meta.env.VITE_LOGIN_URL,
      clientId: oidcClientId,
      lang: languageRef.current,
      debug: false,
      resetPasswordUrl: `${import.meta.env.VITE_LOGIN_URL}/auth.aspx?return_url=${import.meta.env.VITE_WEBSITE_URL}/reset-password-login`,
      verificationUrl: location.href,
      fwsPasswordlessUrl: `${oidcAuthority}/passwordless`,
      passwordlessRedirectUrl: import.meta.env.VITE_WEBSITE_URL,
      accountStateUrl: `${oidcAuthority}/accountState`,
      ...socialSignupConfiguration,
    };

    // the AuthWidget should be made available via scripts added to the <head>, in the public/index.html file
    onAuthWidgetLoad((authWidget) => (window.loginRadius = authWidget.initAuthentication(config)));

    // these are events fired by the speedy auth widget
    window.addEventListener("registrationsuccess", () => {
      track({ name: "Signup", eventType: "success" });
    });

    window.addEventListener("loginsuccess", () => {
      track({ name: "Login", eventType: "logging in" });
      signinRedirect();
    });

    // when the modal opens/ closes, the html is rerendered which seems to make the google captcha used by the auth widget to go balistic.
    // this stops that from happening by preventing the call from being made if it already has once
    const handleCaptchaError = (event: ErrorEvent) => {
      if (event.message.toLowerCase().includes("captcha")) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const captchaQuery = "script[src*='captcha'], iframe[src*='captcha']";
        const captchaEls = document.querySelectorAll(captchaQuery);

        window.grecaptcha = undefined;
        captchaEls.forEach((e) => e.remove());
        console.warn(event.message);
      }
    };

    window.addEventListener("error", handleCaptchaError);

    return () => {
      window.removeEventListener("error", handleCaptchaError);
    };
  }, [languageId, signinRedirect, track]);
};
